import { getSessionId } from './session';
import { serializeLogData } from './sanitize';
import { isPlayerMorphActive } from '@/utils/playerOpenIdle';

const DB_NAME = 'personal-website-logs';
const STORE_NAME = 'entries';
const DB_VERSION = 1;
const MAX_TOTAL_BYTES = 50 * 1024 * 1024;
/** 批量写入 IndexedDB，避免打开播放页等 burst 日志时每条 console 触发一次事务掉帧 */
const FLUSH_IDLE_TIMEOUT_MS = 160;
const FLUSH_MAX_PENDING = 48;

let dbPromise = null;
let totalBytesCache = null;
const pendingEntries = [];
const morphDeferredEntries = [];
let flushScheduled = false;
let flushInProgress = null;

function openDb() {
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

function formatLogLine(level, module, message, data, traceId) {
  const timestamp = new Date().toISOString().replace('T', ' ').slice(0, 19);
  const sessionId = getSessionId() || '-';
  const tracePart = traceId ? `|trace:${traceId}` : '';
  const moduleName = module || 'App';
  const serialized = serializeLogData(data);
  const suffix = serialized ? ` | ${serialized}` : '';
  return `${timestamp} [${level.toUpperCase()}] [sess:${sessionId}${tracePart}|user:] [${moduleName}] ${message}${suffix}`;
}

function estimateEntryBytes(entry) {
  const line = entry.line || '';
  return line.length * 2 + 48;
}

function releaseMorphDeferredLogs() {
  if (morphDeferredEntries.length === 0) {
    return;
  }
  pendingEntries.push(...morphDeferredEntries.splice(0));
  scheduleFlush();
}

async function refreshTotalBytes(db) {
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const request = store.getAll();
  const entries = await new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
  totalBytesCache = entries.reduce((sum, entry) => sum + (entry.bytes || estimateEntryBytes(entry)), 0);
  return totalBytesCache;
}

async function trimOverflow(db) {
  let totalBytes = totalBytesCache ?? await refreshTotalBytes(db);
  if (totalBytes <= MAX_TOTAL_BYTES) {
    return;
  }

  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  const cursorRequest = store.openCursor();
  await new Promise((resolve, reject) => {
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (!cursor || totalBytes <= MAX_TOTAL_BYTES) {
        resolve();
        return;
      }
      totalBytes -= cursor.value.bytes || estimateEntryBytes(cursor.value);
      cursor.delete();
      cursor.continue();
    };
    cursorRequest.onerror = () => reject(cursorRequest.error);
  });
  totalBytesCache = totalBytes;
}

function scheduleFlush() {
  if (flushScheduled || isPlayerMorphActive()) {
    return;
  }
  flushScheduled = true;
  const run = () => {
    flushScheduled = false;
    flushPendingEntries().catch(() => {});
  };
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(run, { timeout: FLUSH_IDLE_TIMEOUT_MS });
  } else {
    setTimeout(run, FLUSH_IDLE_TIMEOUT_MS);
  }
}

async function flushPendingEntries() {
  if (flushInProgress) {
    return flushInProgress;
  }
  if (isPlayerMorphActive()) {
    return;
  }
  if (pendingEntries.length === 0) {
    return;
  }

  const batch = pendingEntries.splice(0, pendingEntries.length);
  flushInProgress = (async () => {
    try {
      const db = await openDb();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        let settled = false;
        const done = (err) => {
          if (settled) return;
          settled = true;
          if (err) reject(err);
          else resolve();
        };
        tx.oncomplete = () => done();
        tx.onerror = () => done(tx.error);
        tx.onabort = () => done(tx.error || new Error('log buffer transaction aborted'));

        for (const entry of batch) {
          store.add(entry);
          totalBytesCache = (totalBytesCache || 0) + entry.bytes;
        }
      });
      await trimOverflow(db);
    } catch {
      // 缓冲失败不影响业务
    } finally {
      flushInProgress = null;
      if (pendingEntries.length > 0) {
        scheduleFlush();
      }
    }
  })();

  return flushInProgress;
}

export async function appendLogBufferEntry({
  level,
  module,
  message,
  data,
  traceId,
  line,
  timestamp,
}) {
  try {
    const createdAt = timestamp || Date.now();
    const entry = {
      timestamp: createdAt,
      level,
      module: module || 'App',
      message: message || '',
      data,
      traceId: traceId || null,
      line: line || formatLogLine(level, module, message, data, traceId),
      bytes: 0,
    };
    entry.bytes = estimateEntryBytes(entry);

    if (isPlayerMorphActive()) {
      morphDeferredEntries.push(entry);
      return;
    }

    releaseMorphDeferredLogs();
    pendingEntries.push(entry);

    if (pendingEntries.length >= FLUSH_MAX_PENDING) {
      await flushPendingEntries();
      return;
    }
    scheduleFlush();
  } catch {
    // 缓冲失败不影响业务
  }
}

export async function getAllBufferedLogEntries() {
  return getBufferedLogEntriesSince(0);
}

/** 按时间下界读取日志（用 timestamp 索引，避免 getAll 整库扫描） */
export async function getBufferedLogEntriesSince(cutoffMs = 0) {
  releaseMorphDeferredLogs();
  await flushPendingEntries();
  const db = await openDb();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const safeCutoff = Number.isFinite(cutoffMs) ? Math.max(0, cutoffMs) : 0;

  const entries = await new Promise((resolve, reject) => {
    const collected = [];
    const index = store.index('timestamp');
    const request = safeCutoff > 0
      ? index.openCursor(IDBKeyRange.lowerBound(safeCutoff))
      : index.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve(collected);
        return;
      }
      collected.push(cursor.value);
      cursor.continue();
    };
    request.onerror = () => reject(request.error);
  });

  return entries.sort((a, b) => a.id - b.id);
}

if (typeof window !== 'undefined') {
  window.addEventListener('player-open-idle', () => {
    releaseMorphDeferredLogs();
  });
}

export { formatLogLine };
